import { useState } from 'react'
import { router, Link, usePage } from '@inertiajs/react'
import { StudentShell } from '@/components/shared/StudentShell'
import { ResumeAttemptModal } from '@/components/student/ResumeAttemptModal'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Layers, ArrowRight, ChevronLeft, Clock, Award, HelpCircle,
  Check, Compass, Globe, Coins, Play, RotateCcw, ArrowUpRight,
  AlertCircle, FileText
} from 'lucide-react'
import type { PageProps } from '@/types'

interface SectionInfo {
  id: number
  name: string
  total_questions: number
  max_attempts: number | null
  duration_minutes: number | null
  subject_hint: string | null
  positive_marks?: number | null
  negative_marks?: number | null
  marking_rule: {
    name: string
    question_type: string
    strategy: string
    positive_marks?: number | null
    negative_marks?: number | null
    params?: Record<string, any>
  } | null
}

interface ExamConfigInfo {
  pre_test_instructions_html?: string
  general_instructions_html?: string
  instructions_html?: string
  timer_type?: string
  calculator_enabled?: boolean
  marked_for_review_rule?: string
  allow_language_switch?: boolean
  primary_language?: string
  auto_submit_on_expiry?: boolean
  negative_marking_enabled?: boolean
  positive_marks?: number | null
  negative_marks?: number | null
}

interface AttemptData {
  attempts_count: number
  has_attempted: boolean
  latest_attempt_id?: number
  latest_status?: string
  latest_score?: number
  best_score?: number
  max_score?: number
  accuracy?: number
  completed_at?: string
}

interface TestInfo {
  id: number
  title: string
  description?: string
  test_type?: string
  language?: string
  is_free?: boolean
  credit_cost?: number
  has_access?: boolean
  enrolled?: boolean
  attempt_data?: AttemptData
  duration_minutes: number | null
  question_count: number
  total_marks: number | null
  positive_marks?: number | null
  negative_marks?: number | null
  negative_marking_enabled?: boolean
  exam?: { id: number; name: string; code: string } | null
  max_attempts?: number | null
}

interface Props {
  test: TestInfo
  credit_balance?: number
  exam_config?: ExamConfigInfo | null
  sections: SectionInfo[]
}

export default function StudentTestInstructions({ test, credit_balance = 0, exam_config, sections = [] }: Props) {
  const { ongoing_attempt } = usePage<PageProps>().props
  const [agreed, setAgreed] = useState(false)
  const [starting, setStarting] = useState(false)
  const [unlocking, setUnlocking] = useState(false)
  const [conflictModalOpen, setConflictModalOpen] = useState(false)

  const initiateTest = () => {
    setStarting(true)
    router.post(`/student/tests/${test.id}/attempts`, {}, {
      onFinish: () => setStarting(false)
    })
  }

  const handleStartTest = () => {
    if (!agreed || starting) return

    if (ongoing_attempt) {
      setConflictModalOpen(true)
      return
    }

    initiateTest()
  }

  const handlePurchase = () => {
    setUnlocking(true)
    router.post(`/student/tests/${test.id}/purchase`, {}, {
      onFinish: () => setUnlocking(false)
    })
  }

  const isUntimed =
    exam_config?.timer_type === 'none' ||
    test.test_type === 'practice' ||
    test.duration_minutes == null ||
    test.duration_minutes === 0 ||
    test.title.toLowerCase().includes('practice') ||
    (!test.duration_minutes && !sections.some(s => (s.duration_minutes || 0) > 0))
  const hasSectionDurations = sections.some(s => (s.duration_minutes || 0) > 0)
  const isSectional =
    !isUntimed &&
    exam_config?.timer_type !== 'global' &&
    (exam_config?.timer_type === 'sectional' || hasSectionDurations) &&
    hasSectionDurations
  const isNegMarking = test.negative_marking_enabled ?? (exam_config?.negative_marking_enabled ?? (Number(test.negative_marks) > 0))
  const posMark = test.positive_marks ?? exam_config?.positive_marks ?? 1.0
  const negMark = Math.abs(Number(test.negative_marks ?? exam_config?.negative_marks ?? 0.25))
  const sectionsWithCaps = sections.filter(s => Boolean(s.max_attempts && s.max_attempts < s.total_questions))
  const isAnySectionCapped = sectionsWithCaps.length > 0
  const isTestAttemptCapped = Boolean(test.max_attempts && test.max_attempts < test.question_count)

  const reviewRule = exam_config?.marked_for_review_rule || 'counts_if_answered'
  const testDescription = test.description?.trim() || ''
  const candidateInstructions = exam_config?.pre_test_instructions_html?.trim() || exam_config?.instructions_html?.trim() || ''

  const languageCode = (test.language || exam_config?.primary_language || 'en').toUpperCase()
  const languageName = languageCode === 'EN' ? 'English' : (languageCode === 'HI' ? 'Hindi' : languageCode)
  const testTypeLabel = test.test_type ? test.test_type.replace(/_/g, ' ') : 'Mock test'

  const hasAccess = Boolean(test.has_access ?? test.is_free)
  const hasAttempted = Boolean(test.attempt_data?.has_attempted && test.attempt_data.attempts_count > 0)
  const isOngoingCurrentTest = Boolean(ongoing_attempt && ongoing_attempt.test_id === test.id)

  return (
    <StudentShell>
      <div className="space-y-4 sm:space-y-5 max-w-6xl mx-auto pb-32 sm:pb-36 lg:pb-16">
        {/* Desktop Breadcrumbs & Wallet Balance */}
        <div className="hidden md:flex items-center justify-between gap-3 text-xs">
          <nav className="flex items-center gap-1.5 text-muted-foreground">
            <Link href="/student/dashboard" className="hover:text-foreground transition-colors">
              Student portal
            </Link>
            <span className="text-muted-foreground/50">›</span>
            <Link href="/student/tests" className="hover:text-foreground transition-colors">
              Tests
            </Link>
            <span className="text-muted-foreground/50">›</span>
            <span className="text-foreground font-medium truncate">
              {test.exam?.name || test.title}
            </span>
          </nav>

          <Link
            href="/student/credits"
            className="flex items-center gap-1.5 bg-tertiary/10 border border-tertiary/30 px-3 py-1 rounded-full text-xs font-semibold text-tertiary hover:bg-tertiary/20 transition-all shadow-2xs"
          >
            <Coins className="h-3.5 w-3.5" />
            <span>{credit_balance} Credits</span>
          </Link>
        </div>

        {/* Mobile Header Bar */}
        <div className="flex md:hidden items-center justify-between gap-2.5 pt-1">
          <div className="flex items-center gap-2.5">
            <Button
              variant="outline"
              size="icon"
              asChild
              className="h-9 w-9 rounded-full border-border bg-card text-foreground shrink-0 shadow-2xs"
            >
              <Link href={test.exam ? `/student/exams/${test.exam.id}` : `/student/tests`}>
                <ChevronLeft className="h-5 w-5" />
              </Link>
            </Button>
            <h1 className="text-base font-bold text-foreground">
              Test instructions
            </h1>
          </div>

          <Link
            href="/student/credits"
            className="flex items-center gap-1 bg-tertiary/10 border border-tertiary/30 px-2.5 py-1 rounded-full text-[11px] font-semibold text-tertiary shadow-2xs"
          >
            <Coins className="h-3 w-3" />
            <span>{credit_balance}</span>
          </Link>
        </div>

        {/* Unenrolled Warning Banner (if applicable) */}
        {test.enrolled === false && test.exam && (
          <div className="rounded-2xl border border-tertiary/40 bg-tertiary/10 p-3.5 sm:p-4 flex items-center justify-between gap-3 text-xs shadow-glass-xs">
            <div className="flex items-center gap-2.5 text-foreground font-medium">
              <AlertCircle className="h-4 w-4 text-tertiary shrink-0" />
              <span>You are not enrolled in <strong>{test.exam.name}</strong>. Enroll to track your ranks on the leaderboard.</span>
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs font-bold rounded-xl shrink-0" asChild>
              <Link href={`/student/exams/${test.exam.id}`}>Enroll Now</Link>
            </Button>
          </div>
        )}

        {/* Hero Card */}
        <div className="rounded-2xl border border-border/70 bg-card p-4 sm:p-6 shadow-xs space-y-3 sm:space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider bg-muted text-muted-foreground px-2.5 py-0.5 rounded-md border border-border/60">
              {languageCode}
            </span>
            <span className="text-[11px] font-bold capitalize bg-secondary/15 text-secondary px-2.5 py-0.5 rounded-md border border-secondary/30">
              {testTypeLabel}
            </span>
            {hasAccess ? (
              <span className="text-[11px] font-bold bg-primary/15 text-primary px-2.5 py-0.5 rounded-md border border-primary/30 flex items-center gap-1">
                <Check className="h-3 w-3" />
                <span>{test.is_free ? 'Free access' : 'Unlocked access'}</span>
              </span>
            ) : (
              <span className="text-[11px] font-bold bg-tertiary/15 text-tertiary px-2.5 py-0.5 rounded-md border border-tertiary/30 flex items-center gap-1">
                <Coins className="h-3 w-3" />
                <span>{test.credit_cost} Credits Required</span>
              </span>
            )}
            {hasAttempted && (
              <span className="text-[11px] font-bold bg-secondary/15 text-secondary px-2.5 py-0.5 rounded-md border border-secondary/30 flex items-center gap-1">
                <RotateCcw className="h-3 w-3" />
                <span>{test.attempt_data?.attempts_count} {test.attempt_data?.attempts_count === 1 ? 'Attempt' : 'Attempts'}</span>
              </span>
            )}
          </div>

          <div className="space-y-1">
            <h1 className="text-lg sm:text-2xl font-bold text-foreground tracking-tight">
              {test.title}
            </h1>
            {testDescription && (
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                {testDescription}
              </p>
            )}
          </div>

          {/* Mobile Quick Stats Tiles (matching Image 1) */}
          <div className="grid grid-cols-4 gap-2 pt-1 md:hidden">
            <div className="p-2 sm:p-2.5 rounded-xl bg-muted/40 border border-border/60 text-center">
              <div className="text-xs font-extrabold text-foreground">{isUntimed ? 'No Limit' : (test.duration_minutes ? `${test.duration_minutes}m` : 'Untimed')}</div>
              <div className="text-[10px] text-muted-foreground font-medium mt-0.5">Duration</div>
            </div>
            <div className="p-2 sm:p-2.5 rounded-xl bg-muted/40 border border-border/60 text-center">
              <div className="text-xs font-extrabold text-foreground">{test.total_marks || (test.question_count * 4)}</div>
              <div className="text-[10px] text-muted-foreground font-medium mt-0.5">Marks</div>
            </div>
            <div className="p-2 sm:p-2.5 rounded-xl bg-muted/40 border border-border/60 text-center">
              <div className="text-xs font-extrabold text-foreground">{test.question_count}</div>
              <div className="text-[10px] text-muted-foreground font-medium mt-0.5">Questions</div>
            </div>
            <div className="p-2 sm:p-2.5 rounded-xl bg-muted/40 border border-border/60 text-center">
              <div className="text-xs font-extrabold text-foreground">{languageCode}</div>
              <div className="text-[10px] text-muted-foreground font-medium mt-0.5">Language</div>
            </div>
          </div>
        </div>

        {/* 2-Column Desktop Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5 items-start">
          {/* Main Left Content Column */}
          <div className="lg:col-span-8 space-y-3.5 sm:space-y-4">
            {/* Candidate Instructions from Examiner Card */}
            {candidateInstructions && (
              <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 sm:p-5 shadow-xs space-y-2">
                <div className="flex items-center gap-2 text-primary font-bold text-sm">
                  <FileText className="h-4 w-4 shrink-0" />
                  <span>Important Candidate Instructions</span>
                </div>
                <div
                  className="text-xs sm:text-sm text-foreground/90 leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: candidateInstructions }}
                />
              </div>
            )}

            {/* Card 1: Sections and pattern */}
            <div className="rounded-2xl border border-border/70 bg-card p-4 sm:p-5 shadow-xs space-y-3.5">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-primary shrink-0" />
                <h2 className="font-bold text-sm text-foreground">
                  Sections and pattern
                </h2>
              </div>

              {sections.length > 0 ? (
                <>
                  {/* Desktop Table View */}
                  <div className="hidden sm:block border border-border/70 rounded-xl overflow-hidden text-xs">
                    <table className="w-full text-left">
                      <thead className="bg-muted/40 text-muted-foreground font-bold border-b border-border/70 uppercase text-[10px] tracking-wider">
                        <tr>
                          <th className="py-2.5 px-3">Section</th>
                          <th className="py-2.5 px-3 text-center">Qs</th>
                          <th className="py-2.5 px-3 text-center">Attempts</th>
                          <th className="py-2.5 px-3 text-center">Timer</th>
                          <th className="py-2.5 px-3 text-right">Marking</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {sections.map((sec) => {
                          const rule = sec.marking_rule
                          const pos = sec.positive_marks ?? rule?.positive_marks ?? rule?.params?.correct ?? rule?.params?.full_correct ?? test.positive_marks ?? 1
                          const neg = sec.negative_marks ?? rule?.negative_marks ?? rule?.params?.wrong ?? rule?.params?.any_wrong ?? test.negative_marks ?? 0
                          const negVal = Math.abs(Number(neg) || 0)
                          const isCompulsory = !sec.max_attempts || sec.max_attempts >= sec.total_questions

                          return (
                            <tr key={sec.id} className="hover:bg-muted/10 transition-colors">
                              <td className="py-2.5 px-3 font-semibold text-foreground">
                                {sec.name}
                                {sec.subject_hint && (
                                  <span className="text-[10px] text-muted-foreground block font-normal">{sec.subject_hint}</span>
                                )}
                              </td>
                              <td className="py-2.5 px-3 text-center font-medium">{sec.total_questions}</td>
                              <td className="py-2.5 px-3 text-center text-muted-foreground">
                                {isCompulsory ? 'Compulsory' : `Any ${sec.max_attempts}`}
                              </td>
                              <td className="py-2.5 px-3 text-center text-muted-foreground">
                                {sec.duration_minutes ? `${sec.duration_minutes}m` : (isUntimed ? 'No Limit' : 'Shared')}
                              </td>
                              <td className="py-2.5 px-3 text-right font-semibold font-mono">
                                <span className="text-primary font-bold">+{pos}</span>
                                <span className="text-muted-foreground font-normal"> / </span>
                                <span className={negVal > 0 ? 'text-destructive font-bold' : 'text-muted-foreground'}>
                                  {negVal > 0 ? `-${negVal}` : '0'}
                                </span>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Compact List View (matching Image 1) */}
                  <div className="sm:hidden space-y-2 text-xs text-muted-foreground leading-relaxed">
                    {sections.map((sec) => {
                      const rule = sec.marking_rule
                      const pos = sec.positive_marks ?? rule?.positive_marks ?? rule?.params?.correct ?? rule?.params?.full_correct ?? test.positive_marks ?? 1
                      const neg = sec.negative_marks ?? rule?.negative_marks ?? rule?.params?.wrong ?? rule?.params?.any_wrong ?? test.negative_marks ?? 0
                      const negVal = Math.abs(Number(neg) || 0)
                      const isCompulsory = !sec.max_attempts || sec.max_attempts >= sec.total_questions

                      return (
                        <div key={sec.id} className="p-3 rounded-xl bg-muted/20 border border-border/50 space-y-1">
                          <div className="font-semibold text-foreground">{sec.name}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {sec.total_questions} questions, {isCompulsory ? 'all compulsory' : `attempt any ${sec.max_attempts}`} · {sec.duration_minutes ? `${sec.duration_minutes}m timer` : (isUntimed ? 'no time limit' : 'shared timer')} ·{' '}
                            <span className="font-mono font-bold text-primary">+{pos}</span>
                            <span> / </span>
                            <span className={`font-mono font-bold ${negVal > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                              {negVal > 0 ? `-${negVal}` : '0'}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              ) : (
                <div className="text-xs text-muted-foreground">
                  Standard full paper format with {test.question_count} compulsory questions.
                </div>
              )}
            </div>

            {/* Card 2: Timing and auto-submission */}
            <div className="rounded-2xl border border-border/70 bg-card p-4 sm:p-5 shadow-xs space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary shrink-0" />
                <h2 className="font-bold text-sm text-foreground">
                  {isUntimed ? 'Timing and submission' : isSectional ? 'Sectional timing and auto-submission' : 'Global timing and auto-submission'}
                </h2>
              </div>
              <ul className="space-y-2.5 text-xs text-muted-foreground leading-relaxed">
                <li className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                  <span>
                    {isUntimed ? (
                      <>
                        <strong className="text-foreground font-semibold">No time limit:</strong> This test has no duration limit or countdown pressure. You can solve questions and practice at your own pace.
                      </>
                    ) : isSectional ? (
                      <>
                        <strong className="text-foreground font-semibold">Dedicated section timers:</strong> Each section runs on its own independent countdown ({sections.map(s => `${s.name}: ${s.duration_minutes ? `${s.duration_minutes}m` : 'shared'}`).join(', ')}). Time cannot be transferred between sections.
                      </>
                    ) : (
                      <>
                        <strong className="text-foreground font-semibold">Global test countdown:</strong> The test runs on a single overall timer of <strong className="text-foreground font-semibold">{test.duration_minutes || 60} minutes</strong> shared across all sections.
                      </>
                    )}
                  </span>
                </li>

                <li className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                  <span>
                    {isUntimed ? (
                      <>
                        <strong className="text-foreground font-semibold">Manual submission:</strong> There is no automatic submission timeout. When you have completed all questions you wish to answer, simply click <strong className="text-foreground font-semibold">Submit Test</strong> to finalize your attempt.
                      </>
                    ) : isSectional ? (
                      <>
                        <strong className="text-foreground font-semibold">Section locking & final submission:</strong> When a section timer expires, that section locks automatically and the test advances to the next section. When the final section ends, all responses are automatically submitted.
                      </>
                    ) : (
                      <>
                        <strong className="text-foreground font-semibold">Auto-submission on expiry:</strong> When the global timer reaches 00:00, all saved responses are submitted automatically. You may also submit early whenever you finish.
                      </>
                    )}
                  </span>
                </li>

                <li className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-foreground font-semibold">Real-time cloud autosave:</strong> Every answer selection, clear, or review status is automatically synced to the server instantly as you navigate.
                  </span>
                </li>

                {exam_config?.calculator_enabled && (
                  <li className="flex items-start gap-2">
                    <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-foreground font-semibold">On-screen calculator:</strong> An official scientific calculator tool is enabled for this test and accessible from the top toolbar.
                    </span>
                  </li>
                )}

                {exam_config?.allow_language_switch && (
                  <li className="flex items-start gap-2">
                    <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-foreground font-semibold">Language switching:</strong> You can switch question language dynamically during the test without losing recorded answers.
                    </span>
                  </li>
                )}
              </ul>
            </div>

            {/* Card 3: Marking scheme */}
            <div className="rounded-2xl border border-border/70 bg-card p-4 sm:p-5 shadow-xs space-y-3">
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-primary shrink-0" />
                <h2 className="font-bold text-sm text-foreground">
                  Marking scheme
                </h2>
              </div>
              <ul className="space-y-2.5 text-xs text-muted-foreground leading-relaxed">
                <li className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-foreground font-semibold">Correct response:</strong> Awards <strong className="text-primary font-semibold">+{posMark} mark</strong> per correct answer{sections.some(s => s.positive_marks && s.positive_marks !== posMark) ? ' (see section pattern table for section-specific variations)' : ''}.
                  </span>
                </li>

                <li className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                  <span>
                    {isNegMarking && negMark > 0 ? (
                      <>
                        <strong className="text-foreground font-semibold">Negative marking:</strong> Deducts <strong className="text-destructive font-semibold">-{negMark} marks</strong> for each incorrect response ({((negMark / posMark) * 100).toFixed(0)}% penalty). Choose responses with care.
                      </>
                    ) : (
                      <>
                        <strong className="text-foreground font-semibold">No negative marking:</strong> Incorrect answers carry zero penalty (0 marks deducted). Attempting every question is recommended.
                      </>
                    )}
                  </span>
                </li>

                <li className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-foreground font-semibold">Unattempted questions:</strong> Score 0 marks and carry no penalty.
                  </span>
                </li>

                <li className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-foreground font-semibold">Marked for review scoring:</strong>{' '}
                    {reviewRule === 'counts_if_answered'
                      ? 'Questions where an answer is selected AND flagged for review WILL BE SCORED as valid attempts upon submission.'
                      : 'Questions flagged for review will NOT be evaluated unless unmarked before submission.'}
                  </span>
                </li>
              </ul>
            </div>

            {/* Card 4: Answering and navigation */}
            <div className="rounded-2xl border border-border/70 bg-card p-4 sm:p-5 shadow-xs space-y-3">
              <div className="flex items-center gap-2">
                <Compass className="h-4 w-4 text-primary shrink-0" />
                <h2 className="font-bold text-sm text-foreground">
                  Answering and navigation
                </h2>
              </div>
              <ul className="space-y-2.5 text-xs text-muted-foreground leading-relaxed">
                <li className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                  <span>
                    {isSectional ? (
                      <>
                        <strong className="text-foreground font-semibold">Sequential sections:</strong> Sections must be completed in order. Once a section timer expires or you navigate forward, you cannot return to previously locked sections.
                      </>
                    ) : (
                      <>
                        <strong className="text-foreground font-semibold">Free section navigation:</strong> You can freely switch between sections using the tabs at the top at any time during the test.
                      </>
                    )}
                  </span>
                </li>

                {isAnySectionCapped ? (
                  <li className="flex items-start gap-2">
                    <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-foreground font-semibold">Optional question caps:</strong> In section(s){' '}
                      {sectionsWithCaps.map((s, idx) => (
                        <span key={s.id}>
                          {idx > 0 && ', '}
                          <span className="font-semibold text-foreground">
                            {s.name} (max {s.max_attempts} of {s.total_questions})
                          </span>
                        </span>
                      ))}
                      , the question palette tracks your count. You cannot answer more than the maximum allowed attempts without clearing a previous answer.
                    </span>
                  </li>
                ) : isTestAttemptCapped ? (
                  <li className="flex items-start gap-2">
                    <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-foreground font-semibold">Overall test cap:</strong> You can attempt at most {test.max_attempts} out of the total {test.question_count} questions.
                    </span>
                  </li>
                ) : (
                  <li className="flex items-start gap-2">
                    <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-foreground font-semibold">Compulsory questions:</strong> All questions in every section are available to answer and can be attempted in any order.
                    </span>
                  </li>
                )}

                <li className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-foreground font-semibold">Selecting & changing answers:</strong> Click any option to record your response. To change an answer, select a different option. To remove your answer, click <strong className="text-foreground font-semibold">Clear Response</strong>.
                  </span>
                </li>

                <li className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-foreground font-semibold">Question palette:</strong> Jump directly to any question using the numeric palette. Status colors indicate Answered (Green), Unanswered (Amber), Marked for Review (Purple), and Not Visited (Muted).
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Desktop Right Sidebar: Quick Facts + Previous Result + Agreement + CTA */}
          <div className="hidden lg:block lg:col-span-4 space-y-3.5">
            {/* Previous Score Snapshot (if attempted) */}
            {hasAttempted && test.attempt_data && (
              <div className="rounded-2xl border border-border/70 bg-card p-4 space-y-2 shadow-xs">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-foreground flex items-center gap-1.5">
                    <Award className="h-3.5 w-3.5 text-secondary" /> Previous Score
                  </span>
                  <span className="font-extrabold text-foreground">
                    {test.attempt_data.latest_score} / {test.attempt_data.max_score} pts
                  </span>
                </div>
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>Accuracy: {test.attempt_data.accuracy ? `${test.attempt_data.accuracy.toFixed(1)}%` : '—'}</span>
                  {test.attempt_data.latest_attempt_id && (
                    <Link
                      href={`/student/attempts/${test.attempt_data.latest_attempt_id}/result`}
                      className="font-bold text-primary hover:underline inline-flex items-center gap-0.5"
                    >
                      <span>View Result</span>
                      <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              </div>
            )}

            <div className="sticky top-6 rounded-2xl border border-border/70 bg-card p-5 space-y-4 shadow-xs">
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                QUICK FACTS
              </h3>

              <div className="space-y-3 text-xs">
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground/70" />
                    <span>Duration</span>
                  </div>
                  <span className="font-bold text-foreground">
                    {isUntimed ? 'No Limit' : (test.duration_minutes ? `${test.duration_minutes}m` : 'Untimed')}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Award className="h-3.5 w-3.5 text-muted-foreground/70" />
                    <span>Marks</span>
                  </div>
                  <span className="font-bold text-foreground">
                    {test.total_marks || (test.question_count * 4)}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/70" />
                    <span>Questions</span>
                  </div>
                  <span className="font-bold text-foreground">
                    {test.question_count}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground/70" />
                    <span>Language</span>
                  </div>
                  <span className="font-bold text-foreground">
                    {languageName}
                  </span>
                </div>
              </div>

              <div className="border-t border-border/60 pt-4 space-y-4">
                {isOngoingCurrentTest ? (
                  <div className="space-y-2">
                    <Button
                      size="lg"
                      className="w-full h-11 rounded-xl text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-glass-xs cursor-pointer gap-2"
                      asChild
                    >
                      <Link href={ongoing_attempt!.resume_url}>
                        <Play className="h-4 w-4 fill-current" />
                        <span>Resume Ongoing Attempt →</span>
                      </Link>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-xs font-semibold h-9 rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10 cursor-pointer"
                      onClick={() => setConflictModalOpen(true)}
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1" />
                      <span>Abandon & Start New</span>
                    </Button>
                  </div>
                ) : !hasAccess ? (
                  <div className="space-y-2">
                    <Button
                      size="lg"
                      disabled={unlocking}
                      onClick={handlePurchase}
                      className="w-full h-11 rounded-xl text-sm font-bold bg-tertiary text-tertiary-foreground hover:bg-tertiary/90 shadow-glass-xs cursor-pointer gap-2"
                    >
                      <Coins className="h-4 w-4" />
                      <span>{unlocking ? 'Unlocking...' : `Unlock Test (${test.credit_cost} Credits)`}</span>
                    </Button>
                    <p className="text-[11px] text-muted-foreground text-center">
                      Available: {credit_balance} Credits
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start gap-2.5">
                      <Checkbox
                        id="agree-instructions-desktop"
                        checked={agreed}
                        onCheckedChange={(checked) => setAgreed(checked === true)}
                        className="mt-0.5 rounded cursor-pointer"
                      />
                      <Label
                        htmlFor="agree-instructions-desktop"
                        className="text-xs text-foreground font-medium leading-relaxed cursor-pointer select-none"
                      >
                        I agree to the exam guidelines above and confirm I'm ready to begin.
                      </Label>
                    </div>

                    <Button
                      size="lg"
                      disabled={!agreed || starting}
                      onClick={handleStartTest}
                      className="w-full h-11 rounded-xl text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-glass-xs cursor-pointer gap-2 transition-all"
                    >
                      <span>{starting ? 'Starting test...' : 'I am ready to begin'}</span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>

                    <p className="text-[11px] text-muted-foreground text-center">
                      {isUntimed
                        ? 'No time limit · Practice at your own pace.'
                        : isSectional
                        ? 'Sections run on dedicated timers and cannot be paused.'
                        : "You can't pause once the timer starts."}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Fixed Bottom Action Bar */}
        <div className="lg:hidden fixed bottom-[54px] left-0 right-0 z-20 border-t border-border/80 bg-card/95 backdrop-blur-md px-3.5 py-2.5 shadow-glass-md">
          <div className="max-w-md mx-auto space-y-2">
            {isOngoingCurrentTest ? (
              <div className="space-y-1.5">
                <Button
                  size="lg"
                  className="w-full h-10 rounded-xl text-xs font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-glass-xs cursor-pointer gap-2"
                  asChild
                >
                  <Link href={ongoing_attempt!.resume_url}>
                    <Play className="h-4 w-4 fill-current" />
                    <span>Resume Ongoing Attempt →</span>
                  </Link>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-xs font-semibold h-8 rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10 cursor-pointer"
                  onClick={() => setConflictModalOpen(true)}
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1" />
                  <span>Abandon & Start New</span>
                </Button>
              </div>
            ) : !hasAccess ? (
              <div className="space-y-1.5">
                <Button
                  size="lg"
                  disabled={unlocking}
                  onClick={handlePurchase}
                  className="w-full h-10 rounded-xl text-xs font-bold bg-tertiary text-tertiary-foreground hover:bg-tertiary/90 shadow-glass-xs cursor-pointer gap-2"
                >
                  <Coins className="h-4 w-4" />
                  <span>{unlocking ? 'Unlocking...' : `Unlock Test (${test.credit_cost} Credits)`}</span>
                </Button>
                <p className="text-[10px] text-muted-foreground text-center">
                  Available: {credit_balance} Credits
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="agree-instructions-mobile"
                    checked={agreed}
                    onCheckedChange={(checked) => setAgreed(checked === true)}
                    className="h-4 w-4 rounded cursor-pointer shrink-0 mt-0"
                  />
                  <Label
                    htmlFor="agree-instructions-mobile"
                    className="text-[11px] text-foreground font-medium leading-tight cursor-pointer select-none truncate"
                  >
                    I agree to the guidelines & confirm I'm ready.
                  </Label>
                </div>

                <Button
                  size="lg"
                  disabled={!agreed || starting}
                  onClick={handleStartTest}
                  className="w-full h-10 rounded-xl text-xs font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-glass-xs cursor-pointer gap-2 transition-all"
                >
                  <span>{starting ? 'Starting test...' : 'I am ready to begin'}</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>

                <p className="text-[10px] text-muted-foreground text-center truncate">
                  {isUntimed
                    ? 'No time limit · Practice at your own pace.'
                    : isSectional
                    ? 'Sections run on dedicated timers and cannot be paused.'
                    : "You can't pause once the timer starts."}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <ResumeAttemptModal
        open={conflictModalOpen}
        onOpenChange={setConflictModalOpen}
        ongoingAttempt={ongoing_attempt || null}
        onAbandonSuccess={() => initiateTest()}
      />
    </StudentShell>
  )
}
